import React, { useState, useEffect, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';
import ProductSelector from './ProductSelector';
import { apiPost } from '../services/apiClient';

const NPSMovingAverage = ({ topicMap, companyName,
    // Optional controlled product selection, so a parent (e.g. the Daily
    // Trackers tab switcher) can keep the selected topic sticky across
    // different chart types. Falls back to internal state when omitted.
    selectedProduct: controlledSelectedProduct, onProductChange }) => {
    const [data, setData] = useState([]);
    const [internalSelectedProduct, setInternalSelectedProduct] = useState('');
    const isProductControlled = controlledSelectedProduct !== undefined && typeof onProductChange === 'function';
    const selectedProduct = isProductControlled ? controlledSelectedProduct : internalSelectedProduct;
    const setSelectedProduct = isProductControlled ? onProductChange : setInternalSelectedProduct;
    const [isLoading, setIsLoading] = useState(false);
    const [startDate, setStartDate] = useState('2024-01-01');
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [isHeaderHovered, setIsHeaderHovered] = useState(false);

    // Use topicMap directly from props, with auto-select logic
    const filteredTopicMap = useMemo(() => {
        if (!topicMap) return {};

        // Auto-select first available product if none selected
        if (Object.keys(topicMap).length > 0 && !selectedProduct) {
            const sortedTopics = Object.values(topicMap).sort((a, b) => {
                const priorityA = a.priority === undefined ? Infinity : a.priority;
                const priorityB = b.priority === undefined ? Infinity : b.priority;
                return priorityA - priorityB;
            });

            if (sortedTopics[0]) {
                setSelectedProduct(sortedTopics[0].id);
            }
        }

        return topicMap;
    }, [topicMap, selectedProduct]);

    useEffect(() => {
        const fetchData = async () => {
            if (!selectedProduct) return;

            setIsLoading(true);
            try {
                const response = await apiPost('topic/nps/moving-average', {
                    topic_id: selectedProduct,
                    start_date: startDate,
                    end_date: endDate,
                    promoter_threshold: 8,
                    detractor_threshold: 6,
                });

                if (!response.ok) {
                    throw new Error(`Backend error: ${response.status}`);
                }

                const json = await response.json();
                const rawData = json.data ?? json;

                console.log('Raw data received:', rawData);
                console.log('Selected product:', selectedProduct);
                console.log('Date range:', {
                    start: startDate,
                    end: endDate
                });
                
                if (!Array.isArray(rawData) || rawData.length === 0) {
                    console.log('No data received or data is empty');
                    setData([]);
                    return;
                }

                // Process the data
                const processedData = rawData.map(item => {
                    // Shift date forward by one day
                    const date = new Date(item.day + 'T00:00:00Z');
                    date.setDate(date.getDate() + 1);
                    return {
                        date: format(date, 'MMM yyyy'),
                        fullDate: item.day,
                        nps: Number(item.mean_nps),
                        lowerCI: Number(item.lower_ci),
                        upperCI: Number(item.upper_ci),
                        count: Number(item.count),
                        isForwardFilled: item.is_forward_filled
                    };
                });

                // Find the last day with actual data (not forward filled)
                let lastActualDataIndex = processedData.length - 1;
                for (let i = processedData.length - 1; i >= 0; i--) {
                    if (!processedData[i].isForwardFilled && processedData[i].count > 0) {
                        lastActualDataIndex = i;
                        break;
                    }
                }

                // Trim data to stop at last actual data point
                const trimmedData = processedData.slice(0, lastActualDataIndex + 1);

                console.log('Raw data from SQL:', rawData[0]);
                console.log('Processed data length:', processedData.length);
                console.log('Trimmed data length:', trimmedData.length);
                console.log('Last actual data index:', lastActualDataIndex);
                setData(trimmedData);
            } catch (error) {
                console.error('Error in fetchData:', error);
                setData([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedProduct, startDate, endDate]);

    const handleProductChange = (productId) => {
        setSelectedProduct(productId);
    };

    const getSelectedProductName = () => {
        if (selectedProduct && filteredTopicMap && filteredTopicMap[selectedProduct]) {
            return filteredTopicMap[selectedProduct].name;
        }
        return 'Product';
    };

    const buildMainTitle = () => {
        return `${getSelectedProductName()} · NPS Moving Average`;
    };

    const buildSubline = () => {
        const dateRange = `${format(parseISO(startDate), 'MMM d, yyyy')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
        return `${dateRange} • 14-Day Rolling Average`;
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length > 0) {
            const dataPoint = payload[0].payload;
            const displayDate = dataPoint.fullDate
                ? format(parseISO(dataPoint.fullDate), 'MMM d, yyyy')
                : label;
            return (
                <div className="custom-tooltip" style={{
                    backgroundColor: 'white',
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                }}>
                    <p><strong>{displayDate}</strong></p>
                    <p>NPS: {dataPoint.nps.toFixed(1)}</p>
                    <p>95% CI: [{dataPoint.lowerCI.toFixed(1)}, {dataPoint.upperCI.toFixed(1)}]</p>
                    <p>Responses: {dataPoint.count}</p>
                    <p style={{ fontSize: '0.9em', color: '#666' }}>14-day moving average</p>
                    {dataPoint.isForwardFilled && (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>No data for this date</p>
                    )}
                </div>
            );
        }
        return null;
    };

    // Add debug log for render
    console.log('NPSMovingAverage rendering with:', {
        hasTopicMap: Object.keys(filteredTopicMap).length > 0,
        selectedProduct,
        dataLength: data.length
    });

    // Consistent control height for all dropdowns
    const CONTROL_HEIGHT = 36;
    const CONTROL_GAP = 12;

    const selectStyle = {
        width: '100%',
        height: CONTROL_HEIGHT,
        '& .MuiSelect-select': {
            paddingTop: '6px',
            paddingBottom: '6px',
            paddingRight: '24px !important',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#d0d0d0',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#999',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1976d2',
        }
    };

    const filterLabelStyle = {
        fontSize: '9px',
        fontWeight: 600,
        color: '#999',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        marginBottom: '3px'
    };

    const dateInputStyle = {
        padding: '6px 10px',
        borderRadius: '4px',
        border: '1px solid #d0d0d0',
        fontSize: '13px',
        height: CONTROL_HEIGHT,
        boxSizing: 'border-box',
        transition: 'border-color 0.15s ease',
        outline: 'none',
        width: '100%'
    };

    return (
        <div style={{ maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Header Card */}
            <div
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
                style={{
                    backgroundColor: '#fafafa',
                    borderRadius: '8px',
                    padding: '20px 24px 18px',
                    marginBottom: '20px',
                    boxShadow: isHeaderHovered
                        ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                        : '0 1px 3px rgba(0, 0, 0, 0.06)',
                    border: '1px solid rgba(0, 0, 0, 0.06)',
                    transition: 'box-shadow 0.2s ease',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                }}
            >
                {/* Title Block */}
                <div style={{ marginBottom: '12px' }}>
                    {/* Company Name - lightest */}
                    <div style={{
                        fontSize: '11px',
                        color: '#777',
                        fontWeight: 500,
                        lineHeight: 1.2,
                        marginBottom: '1px'
                    }}>
                        {companyName || 'Company'}
                    </div>

                    {/* Main H1 */}
                    <h1 style={{
                        fontSize: '22px',
                        fontWeight: 700,
                        margin: 0,
                        color: '#111',
                        lineHeight: 1.25
                    }}>
                        {selectedProduct ? buildMainTitle() : 'Select a Product'}
                    </h1>

                    {/* Context Line - lighter gray */}
                    {selectedProduct && (
                        <div style={{
                            fontSize: '12px',
                            color: '#999',
                            lineHeight: 1.3,
                            marginTop: '2px'
                        }}>
                            {buildSubline()}
                        </div>
                    )}
                </div>

                {/* Filter Bar */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: `${CONTROL_GAP}px`,
                    flexWrap: 'wrap',
                    rowGap: '12px'
                }}>
                    {/* Product */}
                    <div style={{ flex: '1 1 auto', minWidth: 100, maxWidth: 160, boxSizing: 'border-box' }}>
                        <div style={filterLabelStyle}>Product</div>
                        <ProductSelector
                            topicMap={filteredTopicMap}
                            selectedId={selectedProduct || ''}
                            onSelect={handleProductChange}
                            label=""
                            size="small"
                            sx={selectStyle}
                        />
                    </div>

                    {/* Start Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>Start Date</div>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate}
                            style={dateInputStyle}
                        />
                    </div>

                    {/* End Date */}
                    <div style={{ flex: '0 0 auto', minWidth: 110 }}>
                        <div style={filterLabelStyle}>End Date</div>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            style={dateInputStyle}
                        />
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div style={{ marginTop: '8px', width: '100%', height: 400 }}>
                {isLoading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        color: '#666'
                    }}>
                        Loading...
                    </div>
                ) : (
                    <ResponsiveContainer>
                        <LineChart
                            data={data}
                            margin={{
                                top: 5,
                                right: 30,
                                left: 20,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 11 }}
                                interval="preserveStartEnd"
                                tickMargin={10}
                                ticks={(() => {
                                    // Show first occurrence of each month
                                    const seen = new Set();
                                    return data.filter(d => {
                                        if (seen.has(d.date)) return false;
                                        seen.add(d.date);
                                        return true;
                                    }).map(d => d.date);
                                })()}
                            />
                            <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 12 }}
                                label={{
                                    value: 'NPS Score',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: { textAnchor: 'middle' }
                                }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <ReferenceLine y={50} stroke="#666" strokeDasharray="3 3" />
                            <Line
                                type="monotone"
                                dataKey="nps"
                                stroke="#8884d8"
                                name="NPS (14-Day Avg)"
                                dot={false}
                                strokeWidth={2}
                            />
                            <Line
                                type="monotone"
                                dataKey="upperCI"
                                stroke="#82ca9d"
                                name="95% Confidence Interval"
                                dot={false}
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                            <Line
                                type="monotone"
                                dataKey="lowerCI"
                                stroke="#82ca9d"
                                dot={false}
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default NPSMovingAverage; 